import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { useImportContacts } from '../hooks/useImportContacts';
import { guessMapping, ImportField, mapRows, ParsedCsv } from './importCsv';
import { ImportFileStep } from './ImportFileStep';
import { ImportColumnsStep } from './ImportColumnsStep';
import { ImportCheckStep } from './ImportCheckStep';
import { contactsMessages as messages } from './contactsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Outreach area; each part uses its own slice of it */
import styles from './Contacts.module.scss';
/* eslint-enable css-modules/no-unused-class */

type Step = 'file' | 'columns' | 'check';

/**
 * Import CSV (design outreach q3): the file, the column map (guessed from the header names), and
 * the check with "I may contact these people". Imported addresses stay in the workspace and never
 * enter the Music24 directory; suppressed addresses are skipped on the server.
 */
const ImportDialogComponent: React.FC<{ accountId: number; onClose: () => void }> = ({ accountId, onClose }) => {
  const intl = useIntl();
  const { showSuccess } = useShowToast();
  const { importRows, isImporting, error } = useImportContacts(accountId);
  const [step, setStep] = useState<Step>('file');
  const [file, setFile] = useState<{ name: string; csv: ParsedCsv } | null>(null);
  const [mapping, setMapping] = useState<ImportField[]>([]);
  const [consent, setConsent] = useState(false);
  const [listUuid, setListUuid] = useState<string | null>(null);
  const [tag, setTag] = useState('');

  const mapped = useMemo(
    () =>
      file === null ? { rows: [], noEmail: 0 } : mapRows(file.csv, mapping, tag.trim() === '' ? null : tag.trim()),
    [file, mapping, tag]
  );
  const onFile = useCallback((name: string, csv: ParsedCsv) => {
    setFile({ name, csv });
    setMapping(guessMapping(csv.headers));
    setStep('columns');
  }, []);
  const submit = useCallback(() => {
    importRows({ rows: mapped.rows, listUuid })
      .then((result) => {
        showSuccess(intl.formatMessage(messages.done, { ...result }));
        onClose();
      })
      .catch(console.error);
  }, [importRows, intl, listUuid, mapped.rows, onClose, showSuccess]);

  const stepClass = (own: Step, order: Step[]) =>
    clsx(step === own && styles.stepOn, order.indexOf(step) > order.indexOf(own) && styles.stepDone);
  const order: Step[] = ['file', 'columns', 'check'];

  return (
    <Modal isOpen toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        <span className={styles.stepper}>
          <span className={stepClass('file', order)}>{intl.formatMessage(messages.stepFile)}</span>/
          <span className={stepClass('columns', order)}>{intl.formatMessage(messages.stepColumns)}</span>/
          <span className={stepClass('check', order)}>{intl.formatMessage(messages.stepCheck)}</span>
        </span>
        <div>{intl.formatMessage(messages.importTitle)}</div>
      </ModalHeader>
      <ModalBody className="d-flex flex-column gap-3" data-testid="import-dialog">
        {step === 'file' && <ImportFileStep onFile={onFile} />}
        {step === 'columns' && file !== null && (
          <ImportColumnsStep fileName={file.name} csv={file.csv} mapping={mapping} onChange={setMapping} />
        )}
        {step === 'check' && (
          <ImportCheckStep
            accountId={accountId}
            ready={mapped.rows.length}
            noEmail={mapped.noEmail}
            consent={consent}
            onConsent={setConsent}
            listUuid={listUuid}
            onList={setListUuid}
            tag={tag}
            onTag={setTag}
          />
        )}
        {error !== null && <p className="text-danger mb-0">{intl.formatMessage(messages.failed)}</p>}
      </ModalBody>
      <ModalFooter>
        {step !== 'file' && (
          <SMLButton outline omitCap onClick={() => setStep(step === 'check' ? 'columns' : 'file')}>
            {intl.formatMessage(messages.back)}
          </SMLButton>
        )}
        <span className="flex-grow-1 small text-body-secondary">{intl.formatMessage(messages.importsFirst)}</span>
        {step === 'columns' && (
          <SMLButton color="primary" omitCap onClick={() => setStep('check')} disabled={!mapping.includes('email')}>
            {intl.formatMessage(messages.next)}
          </SMLButton>
        )}
        {step === 'check' && (
          <SMLButton
            color="primary"
            omitCap
            onClick={submit}
            isLoading={isImporting}
            disabled={!consent || mapped.rows.length === 0}
          >
            {intl.formatMessage(messages.importButton, { count: mapped.rows.length })}
          </SMLButton>
        )}
      </ModalFooter>
    </Modal>
  );
};

export const ImportDialog = TypedMemo(ImportDialogComponent);
